import { getDefaultSchedule } from "@/containers/Project/components/Schedules/components/helpers/getDefaultSchedule";
import { SearchParameter } from "@/enums";
import { IProject, ISchedule } from "@/interfaces";
import { useProjectContext } from "@/providers";
import { Select } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

const { Option } = Select;

export const ScheduleSelector = () => {
  const intl = useIntl();

  const [searchParams] = useSearchParams();

  const {
    currentSchedule: currentScheduleId,
    setCurrentSchedule,
    project,
  }: {
    currentSchedule: string;
    setCurrentSchedule: Dispatch<SetStateAction<string>>;
    project: IProject;
  } = useProjectContext();

  const [availableSchedules, setAvailableSchedules] = useState<ISchedule[]>([]);

  useEffect(() => {
    const remainingProjectSchedules = project.schedules?.filter(
      ({ isDeleted }) => !isDeleted
    );

    if (!remainingProjectSchedules) return;

    setAvailableSchedules(remainingProjectSchedules);
  }, [project.schedules]);

  useEffect(() => {
    const defaultSchedule = getDefaultSchedule(
      availableSchedules,
      currentScheduleId
    );

    if (!defaultSchedule) return;

    setCurrentSchedule(defaultSchedule.id);
  }, [availableSchedules, currentScheduleId, setCurrentSchedule]);

  useEffect(() => {
    const urlScheduleId = searchParams.get(SearchParameter.Schedule);

    if (!urlScheduleId || !project.schedules?.length || currentScheduleId)
      return;

    if (!project.schedules.find(({ id }) => id === urlScheduleId)) {
      toast.error(
        intl.formatMessage(
          { id: "projects.scheduleNotFound" },
          { scheduleId: urlScheduleId }
        )
      );
      return;
    }

    setCurrentSchedule(urlScheduleId);
  }, [
    currentScheduleId,
    intl,
    project.schedules,
    searchParams,
    setCurrentSchedule,
  ]);

  const handleScheduleChange = (id: string): void => {
    setCurrentSchedule(id);
  };

  if (!currentScheduleId || !availableSchedules?.length) return null;

  return (
    <Select
      data-test-id={`schedule-select-${project.id}`}
      value={currentScheduleId}
      onChange={handleScheduleChange}
      style={{
        width: "160px",
      }}
    >
      {availableSchedules.map(({ id, name }, index) => (
        <Option key={index} value={id}>
          {name || index}
        </Option>
      ))}
    </Select>
  );
};
